import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
} from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { FarmStore } from '../../store/farm.store';
import { UserStore } from '../../store/user.store';

@Directive({
  selector: '[appNotificationVisibilityTracker]',
  standalone: true,
})
export class NotificationVisibilityTrackerDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly userStore = inject(UserStore);
  private readonly farmStore = inject(FarmStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly timestamp = input.required<string>({ alias: 'appNotificationVisibilityTracker' });

  private observer: IntersectionObserver | null = null;
  private static visibleTimestamps = new Set<string>();
  private static userStoreRef: InstanceType<typeof UserStore> | null = null;
  private static farmStoreRef: InstanceType<typeof FarmStore> | null = null;

  // Use RxJS Subject + debounceTime instead of setTimeout for proper cleanup
  private static visibilityChanges$ = new Subject<void>();
  private static debounceSubscription: { unsubscribe: () => void } | null = null;

  ngOnInit(): void {
    NotificationVisibilityTrackerDirective.userStoreRef = this.userStore;
    NotificationVisibilityTrackerDirective.farmStoreRef = this.farmStore;

    // Set up the debounced save subscription if not already set up
    if (!NotificationVisibilityTrackerDirective.debounceSubscription) {
      NotificationVisibilityTrackerDirective.debounceSubscription =
        NotificationVisibilityTrackerDirective.visibilityChanges$
          .pipe(debounceTime(300))
          .subscribe(() => {
            NotificationVisibilityTrackerDirective.saveLatestTimestamp();
          });
    }

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries),
      { threshold: 0.5 }
    );

    this.observer.observe(this.elementRef.nativeElement);
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    entries.forEach(entry => {
      const ts = this.timestamp();
      if (entry.isIntersecting) {
        NotificationVisibilityTrackerDirective.visibleTimestamps.add(ts);
      } else {
        NotificationVisibilityTrackerDirective.visibleTimestamps.delete(ts);
      }
    });

    // Trigger debounced save through Subject
    NotificationVisibilityTrackerDirective.visibilityChanges$.next();
  }

  private static saveLatestTimestamp(): void {
    const timestamps = Array.from(NotificationVisibilityTrackerDirective.visibleTimestamps);
    if (timestamps.length === 0) return;

    const latest = timestamps.sort((a, b) =>
      new Date(b).getTime() - new Date(a).getTime()
    )[0];

    const farmId = NotificationVisibilityTrackerDirective.farmStoreRef?.currentFarm()?.id;
    if (!farmId || !latest) return;

    const currentSaved = NotificationVisibilityTrackerDirective.userStoreRef
      ?.getUserPreferenceValue(farmId, 'global__lastSeenActivityLogTimestamp') as string | null;

    if (!currentSaved || (latest !== currentSaved && new Date(latest) > new Date(currentSaved))) {
      NotificationVisibilityTrackerDirective.userStoreRef
        ?.updateUserPreference(farmId, 'global__lastSeenActivityLogTimestamp', latest);
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
